<script setup>
import { computed, nextTick, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import { useOnixActionsStore } from 'dashboard/store/onix/actions';
import OnixPageLayout from 'dashboard/components-next/onix/OnixPageLayout.vue';
import OnixEmptyState from 'dashboard/components-next/onix/OnixEmptyState.vue';
import ActionCard from 'dashboard/components-next/onix/ActionCard.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const router = useRouter();
const { accountScopedRoute } = useAccount();
const actionsStore = useOnixActionsStore();

const deleteDialogRef = ref(null);
const pendingDelete = ref(null);

const actions = computed(() => actionsStore.getActions);
const flags = computed(() => actionsStore.getUIFlags);

const openNew = () => {
  router.push(accountScopedRoute('onix_actions_new'));
};

const openEdit = id => {
  router.push(accountScopedRoute('onix_actions_edit', { actionId: id }));
};

const askDelete = action => {
  pendingDelete.value = action;
  nextTick(() => deleteDialogRef.value?.open());
};

const confirmDelete = async () => {
  if (!pendingDelete.value) return;
  const ok = await actionsStore.remove(pendingDelete.value.id);
  deleteDialogRef.value?.close();
  pendingDelete.value = null;
  useAlert(ok ? t('ONIX.ACTIONS.DELETED') : t('ONIX.ACTIONS.ERROR_SAVE'));
};

const onCardAction = ({ action, id }) => {
  const record = actions.value.find(item => item.id === id);
  if (action === 'edit') openEdit(id);
  if (action === 'delete' && record) askDelete(record);
};

onMounted(() => {
  actionsStore.fetch();
});
</script>

<template>
  <OnixPageLayout
    :header-title="t('ONIX.ACTIONS.TITLE')"
    :description="t('ONIX.ACTIONS.DESCRIPTION')"
    :button-label="t('ONIX.ACTIONS.ADD')"
    :button-policy="['administrator']"
    :is-fetching="flags.fetchingList"
    :is-empty="!actions.length"
    @click="openNew"
  >
    <template #emptyState>
      <OnixEmptyState
        :title="t('ONIX.ACTIONS.EMPTY_TITLE')"
        :subtitle="t('ONIX.ACTIONS.EMPTY_SUBTITLE')"
        :action-perms="['administrator']"
      >
        <template #preview>
          <div class="flex flex-col gap-4 p-px">
            <ActionCard
              v-for="sample in [
                {
                  title: t('ONIX.ACTIONS.SAMPLE_ONE'),
                  description: t('ONIX.ACTIONS.SAMPLE_ONE_HINT'),
                },
                {
                  title: t('ONIX.ACTIONS.SAMPLE_TWO'),
                  description: t('ONIX.ACTIONS.SAMPLE_TWO_HINT'),
                },
              ]"
              :id="sample.title.length"
              :key="sample.title"
              :title="sample.title"
              :description="sample.description"
              verified
              quiet
            />
          </div>
        </template>
        <template #actions>
          <Button
            :label="t('ONIX.ACTIONS.ADD')"
            icon="i-lucide-plus"
            size="sm"
            @click="openNew"
          />
        </template>
      </OnixEmptyState>
    </template>
    <template #body>
      <div class="flex flex-col gap-4 pb-8">
        <ActionCard
          v-for="action in actions"
          :id="action.id"
          :key="action.id"
          :title="action.title"
          :description="action.description"
          :enabled="action.enabled !== false"
          :verified="!!action.verified_at"
          @open="openEdit(action.id)"
          @action="onCardAction"
        />
      </div>
    </template>
  </OnixPageLayout>

  <Dialog
    ref="deleteDialogRef"
    type="alert"
    :title="t('ONIX.ACTIONS.DELETE_TITLE')"
    :description="t('ONIX.ACTIONS.DELETE_DESCRIPTION')"
    :confirm-button-label="t('ONIX.ACTIONS.DELETE')"
    :is-loading="flags.deletingItem"
    @confirm="confirmDelete"
  />
</template>
